﻿import { GridStack, GridStackOptions, GridStackWidget } from 'gridstack';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fromBase64, toBase64 } from '@tk/utilities/tk.base64';

export default class TKDashboard extends TKCustomElementFactory {
    dashboardGrid: GridStack;
    dashboardConfig: string;
    dashboardUrl: string;
    widgetUrl: string;
    dashboardContactPlanId: string;
    sortEditButton?: HTMLButtonElement;
    isStaticGrid: boolean = true;
    isDirtyGrid: boolean = false;
    sortActiveClassName: string;
    widgetContainer?: HTMLElement;

    constructor() {
        super();
        this.dashboardConfig = this.getAttribute('data-tk-dashboard-config') || '';
        this.dashboardUrl = this.getAttribute('data-tk-dashboard-url') || '';
        this.widgetUrl = this.getAttribute('data-tk-widget-url') || '';
        this.dashboardContactPlanId = this.getAttribute('data-tk-dashboard-cp-id') || '';
        this.sortEditButton = document.querySelector('[data-tk-dashboard-sort-edit]') || undefined;
        this.sortActiveClassName = this.getDataAttribute('sort-active-class-name', 'tk-dashboard--sort-active');
        this.isStaticGrid = !this.classList.contains(this.sortActiveClassName);
        this.widgetContainer = this.querySelector('[data-tk-widget-container]') || undefined;
        this.dashboardGrid = GridStack.init(
            {
                cellHeight: 80,
                staticGrid: this.isStaticGrid,
                columnOpts: {
                    breakpoints: [{ w: 768, c: 1 }],
                    breakpointForWindow: true,
                    layout: 'list',
                    columnMax: 2,
                },
            },
            this.widgetContainer,
        );
        GridStack.renderCB = (element, widget) => {
            element.innerHTML = widget.content || '';
        };
    }

    connectedCallback(): void {
        if (this.dashboardConfig === '') throw new Error('Account: Dashboard config is missing!');
        if (this.dashboardUrl === '') throw new Error('Account: Dashboard Save-Url is missing!');
        if (this.widgetUrl === '') throw new Error('Account: Dashboard Widget-Url is missing!');
        if (this.dashboardContactPlanId === '') throw new Error('Account: Dashboard id is missing!');
        if (!this.widgetContainer) throw new Error('Account: Widget container is missing!');
        this.dashboardConfig = fromBase64(this.dashboardConfig);
        this.initDashboard();

        if (!this.sortEditButton) return;
        this.registerToggleListener();
    }

    initDashboard(): void {
        const serializedData = JSON.parse(this.dashboardConfig);
        serializedData.forEach((widget: GridStackWidget) => {
            const item = {
                ...widget,
                content: `
                    <tk-dashboard-widget
                        class="tk-widget"
                        data-tk-widget-url="${this.widgetUrl}"
                        data-tk-widget-id="${widget.id}"
                    ></tk-dashboard-widget>`,
            };
            this.dashboardGrid.addWidget(item);
        });
    }

    registerToggleListener() {
        const onToggle = this.triggerGridEvent.bind(this);
        this.pushListener({ event: 'click', element: this.sortEditButton!, action: onToggle });
    }

    triggerGridEvent(): void {
        this.isStaticGrid = !this.isStaticGrid;
        this.dashboardGrid.setStatic(this.isStaticGrid);
        this.classList.toggle(this.sortActiveClassName, !this.isStaticGrid);

        if (this.isStaticGrid) {
            this.dashboardGrid.off('change');
            this.style.border = 'none';

            if (!this.isDirtyGrid) return;
            const widgetList = this.dashboardGrid.save(false);
            this.saveDashboardConfig(widgetList);
            this.isDirtyGrid = false;
        } else {
            this.dashboardGrid.on('change', () => {
                this.isDirtyGrid = true;
            });
        }
    }

    saveDashboardConfig(widgetList: GridStackWidget[] | GridStackOptions): void {
        const data = {
            config: JSON.stringify(widgetList),
            cpId: this.dashboardContactPlanId,
        };

        fetchRequest({
            requestURL: this.dashboardUrl,
            resolveHandler: () => {},
            payload: data,
        }).finally(() => {
            this.setAttribute('data-tk-dashboard-config', toBase64(data.config));
        });
    }
}
